import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  ExpandMore,
  Person,
  LocalHospital,
  CalendarToday,
  AccessTime,
  Description,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listWardRounds,
  createWardRound,
  updateWardRound,
  deleteWardRound,
  getTodayRounds,
  createLabOrderFromRound,
  createXrayFromRound,
  createUltrasoundFromRound,
  listWardRoundTemplates,
  uploadWardRoundAttachment,
} from "../../../../api/ipd";
import useFetchObjects from "../../../../api/useFetchObjects";

const WardRoundsList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const apiBase = process.env.REACT_APP_API_URL || "";
  const mediaBase = apiBase.replace(/\/?api\/?$/, "");
  const toAbsolute = (url) => {
    if (!url) return url;
    return url.startsWith("http")
      ? url
      : `${mediaBase}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  // State
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [admissionFilter, setAdmissionFilter] = useState("all");
  const [doctorFilter, setDoctorFilter] = useState("all");
  const [roundDialogOpen, setRoundDialogOpen] = useState(false);
  const [editingRound, setEditingRound] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);

  // Order dialog state
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderRound, setOrderRound] = useState(null);
  const [orderForm, setOrderForm] = useState({
    kind: "lab", // lab | xray | ultrasound
    urgency: false,
    discount: 0,
    tests: [],
    xray_type: "",
    doctor_notes: "",
    is_urgent: false,
    ultrasound_type: "",
    total_amount: "",
  });
  const [testSearch, setTestSearch] = useState("");
  const [selectedLabTests, setSelectedLabTests] = useState([]);

  // Form state
  const [roundForm, setRoundForm] = useState({
    admission: "",
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
    temperature: "",
    blood_pressure_systolic: "",
    blood_pressure_diastolic: "",
    heart_rate: "",
    respiratory_rate: "",
    oxygen_saturation: "",
    nursing_notes: "",
    medication_changes: "",
    special_instructions: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  // State for admission search
  const [admissionSearch, setAdmissionSearch] = useState("");
  const [debouncedAdmissionSearch, setDebouncedAdmissionSearch] = useState("");
  
  // Debounce admission search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAdmissionSearch(admissionSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [admissionSearch]);

  // Fetch data with search
  const { data: admissions, isLoading: admissionsLoading } = useFetchObjects(
    ["ipd-admissions", debouncedAdmissionSearch],
    `ipd-admissions/?status=1${debouncedAdmissionSearch ? `&search=${encodeURIComponent(debouncedAdmissionSearch)}` : ""}`, // Only current admissions with search
    token
  );

  const { data: doctors } = useFetchObjects(
    ["users"],
    "users", // Assuming position 1 is doctors
    token
  );

  // Lookup data for orders
  const { data: labTests } = useFetchObjects(
    ["laboratory-tests", testSearch],
    `laboratory-tests?page_size=50&search=${encodeURIComponent(
      testSearch || ""
    )}`,
    token
  );
  // Round templates
  const { data: templates } = useFetchObjects(
    ["ward-round-templates"],
    "ward-rounds/templates?page_size=1000",
    token
  );
  const { data: xrayTypes } = useFetchObjects(
    ["xray-types"],
    "xray-types?page_size=1000",
    token
  );
  const { data: ultrasoundTypes } = useFetchObjects(
    ["ultrasound-types"],
    "ultrasound-types?page_size=1000",
    token
  );

  // Fetch ward rounds
  const {
    data: roundsData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      "ward-rounds",
      page,
      rowsPerPage,
      searchTerm,
      admissionFilter,
      doctorFilter,
    ],
    queryFn: () =>
      listWardRounds(
        {
          page,
          page_size: rowsPerPage,
          search: searchTerm,
          admission: admissionFilter !== "all" ? admissionFilter : undefined,
          doctor: doctorFilter !== "all" ? doctorFilter : undefined,
        },
        token
      ),
  });

  // Today's rounds
  const { data: todayRounds } = useQuery({
    queryKey: ["today-rounds"],
    queryFn: () => getTodayRounds({}, token),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => createWardRound(data, token),
    onSuccess: async (created) => {
      queryClient.invalidateQueries(["ward-rounds"]);
      queryClient.invalidateQueries(["today-rounds"]);
      // upload attachments if any
      if (selectedFiles && selectedFiles.length && created?.id) {
        for (const file of selectedFiles) {
          const fd = new FormData();
          fd.append("file", file);
          try {
            await uploadWardRoundAttachment(created.id, fd, token);
          } catch {}
        }
      }
      setRoundDialogOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateWardRound(id, data, token),
    onSuccess: async (updated) => {
      queryClient.invalidateQueries(["ward-rounds"]);
      queryClient.invalidateQueries(["today-rounds"]);
      // upload attachments if any
      const roundId = updated?.id || editingRound?.id;
      if (roundId && selectedFiles && selectedFiles.length) {
        for (const file of selectedFiles) {
          const fd = new FormData();
          fd.append("file", file);
          try {
            await uploadWardRoundAttachment(roundId, fd, token);
          } catch {}
        }
      }
      setRoundDialogOpen(false);
      setEditingRound(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteWardRound(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["ward-rounds"]);
      queryClient.invalidateQueries(["today-rounds"]);
      setDeleteDialogOpen(false);
      setSelectedRound(null);
    },
  });

  const labOrderMutation = useMutation({
    mutationFn: ({ id, data }) => createLabOrderFromRound(id, data, token),
    onSuccess: () => {
      setOrderDialogOpen(false);
      setOrderRound(null);
    },
  });

  const xrayOrderMutation = useMutation({
    mutationFn: ({ id, data }) => createXrayFromRound(id, data, token),
    onSuccess: () => {
      setOrderDialogOpen(false);
      setOrderRound(null);
    },
  });

  const ultrasoundOrderMutation = useMutation({
    mutationFn: ({ id, data }) => createUltrasoundFromRound(id, data, token),
    onSuccess: () => {
      setOrderDialogOpen(false);
      setOrderRound(null);
    },
  });

  const resetForm = () => {
    setRoundForm({
      admission: "",
      subjective: "",
      objective: "",
      assessment: "",
      plan: "",
      temperature: "",
      blood_pressure_systolic: "",
      blood_pressure_diastolic: "",
      heart_rate: "",
      respiratory_rate: "",
      oxygen_saturation: "",
      nursing_notes: "",
      medication_changes: "",
      special_instructions: "",
    });
    setFormErrors({});
    setSelectedFiles([]);
    setSelectedTemplateId("");
  };

  const handleOpenDialog = (round = null) => {
    if (round) {
      setEditingRound(round);
      setRoundForm({
        admission: round.admission,
        subjective: round.subjective || "",
        objective: round.objective || "",
        assessment: round.assessment || "",
        plan: round.plan || "",
        temperature: round.temperature || "",
        blood_pressure_systolic: round.blood_pressure_systolic || "",
        blood_pressure_diastolic: round.blood_pressure_diastolic || "",
        heart_rate: round.heart_rate || "",
        respiratory_rate: round.respiratory_rate || "",
        oxygen_saturation: round.oxygen_saturation || "",
        nursing_notes: round.nursing_notes || "",
        medication_changes: round.medication_changes || "",
        special_instructions: round.special_instructions || "",
      });
    } else {
      resetForm();
    }
    setRoundDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setRoundDialogOpen(false);
    setEditingRound(null);
    resetForm();
  };

  const handleSubmit = () => {
    const newErrors = {};

    if (!roundForm.admission)
      newErrors.admission = t("ipd.ward_rounds.errors.admission_required");
    if (!roundForm.subjective)
      newErrors.subjective = t("ipd.ward_rounds.errors.subjective_required");
    if (!roundForm.objective)
      newErrors.objective = t("ipd.ward_rounds.errors.objective_required");
    if (!roundForm.assessment)
      newErrors.assessment = t("ipd.ward_rounds.errors.assessment_required");
    if (!roundForm.plan)
      newErrors.plan = t("ipd.ward_rounds.errors.plan_required");

    setFormErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const submitData = {
        ...roundForm,
        doctor: auth?.user?.user?.id,
      };

      if (editingRound) {
        updateMutation.mutate({ id: editingRound.id, data: submitData });
      } else {
        createMutation.mutate(submitData);
      }
    }
  };

  const handleDelete = (round) => {
    setSelectedRound(round);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedRound) {
      deleteMutation.mutate(selectedRound.id);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t("ipd.ward_rounds.error_loading", "Error loading ward rounds")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          {t("ipd.ward_rounds.title")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          {t("ipd.ward_rounds.add_round")}
        </Button>
      </Box>

      {/* Today's Rounds Summary */}
      {todayRounds && todayRounds.length > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "primary.light",
            color: "primary.contrastText",
          }}
        >
          <Typography variant="h6" gutterBottom>
            {t("ipd.ward_rounds.today_rounds", "Today's Rounds Summary")} (
            {todayRounds.length})
          </Typography>
          <Typography variant="body2">
            {t(
              "ipd.ward_rounds.today_rounds_description",
              "Summary of ward rounds conducted today"
            )}
          </Typography>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder={t("ipd.ward_rounds.search_placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>
                {t("ipd.ward_rounds.filter_by_admission")}
              </InputLabel>
              <Select
                value={admissionFilter}
                onChange={(e) => setAdmissionFilter(e.target.value)}
              >
                <MenuItem value="all">
                  {t("ipd.ward_rounds.all_admissions")}
                </MenuItem>
                {(Array.isArray(admissions)
                  ? admissions
                  : admissions?.results || []
                ).map((admission) => (
                  <MenuItem key={admission.id} value={admission.id}>
                    {admission.patient_name} - {admission.bed_info}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t("ipd.ward_rounds.filter_by_doctor")}</InputLabel>
              <Select
                value={doctorFilter}
                onChange={(e) => setDoctorFilter(e.target.value)}
              >
                <MenuItem value="all">
                  {t("ipd.ward_rounds.all_doctors")}
                </MenuItem>
                {(Array.isArray(doctors)
                  ? doctors
                  : doctors?.results || []
                ).map((doctor) => (
                  <MenuItem key={doctor.id} value={doctor.id}>
                    {doctor.first_name} {doctor.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Ward Rounds List */}
      {(() => {
        const rounds = Array.isArray(roundsData)
          ? roundsData
          : roundsData?.results || [];

        if (rounds.length === 0) {
          return (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t("ipd.ward_rounds.no_rounds_found", "No Ward Rounds Found")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t(
                  "ipd.ward_rounds.no_rounds_description",
                  "There are no ward rounds matching your filters. Try adjusting your search criteria or create a new ward round."
                )}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
              >
                {t("ipd.ward_rounds.add_round")}
              </Button>
            </Paper>
          );
        }

        return (
          <>
            {rounds.map((round) => (
              <Accordion key={round.id} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person color="primary" />
                      <Typography variant="h6">{round.patient_name}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocalHospital color="primary" />
                      <Typography variant="body2">
                        {round.doctor_name}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday color="primary" />
                      <Typography variant="body2">
                        {new Date(round.round_date).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccessTime color="primary" />
                      <Typography variant="body2">
                        {new Date(round.round_date).toLocaleTimeString()}
                      </Typography>
                    </Box>
                    <Box ml="auto">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDialog(round);
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(round);
                        }}
                      >
                        <Delete />
                      </IconButton>
                      <Button
                        size="small"
                        sx={{ ml: 1 }}
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOrderRound(round);
                          setOrderForm({
                            kind: "lab",
                            urgency: false,
                            discount: 0,
                            tests: [],
                            xray_type: "",
                            doctor_notes: "",
                            is_urgent: false,
                            ultrasound_type: "",
                            total_amount: "",
                          });
                          setSelectedLabTests([]);
                          setOrderDialogOpen(true);
                        }}
                      >
                        {t("ipd.ward_rounds.add_order", "Add Order")}
                      </Button>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("ipd.ward_rounds.subjective")}
                          </Typography>
                          <Typography variant="body2">
                            {round.subjective}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("ipd.ward_rounds.objective")}
                          </Typography>
                          <Typography variant="body2">
                            {round.objective}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("ipd.ward_rounds.assessment")}
                          </Typography>
                          <Typography variant="body2">
                            {round.assessment}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t("ipd.ward_rounds.plan")}
                          </Typography>
                          <Typography variant="body2">{round.plan}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    {(round.temperature ||
                      round.blood_pressure_systolic ||
                      round.heart_rate) && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>
                          {t("ipd.ward_rounds.vital_signs")}
                        </Typography>
                        <Grid container spacing={2}>
                          {round.temperature && (
                            <Grid item xs={6} md={2}>
                              <Chip
                                label={`Temp: ${round.temperature}°C`}
                                color="info"
                              />
                            </Grid>
                          )}
                          {round.blood_pressure_systolic &&
                            round.blood_pressure_diastolic && (
                              <Grid item xs={6} md={2}>
                                <Chip
                                  label={`BP: ${round.blood_pressure_systolic}/${round.blood_pressure_diastolic}`}
                                  color="info"
                                />
                              </Grid>
                            )}
                          {round.heart_rate && (
                            <Grid item xs={6} md={2}>
                              <Chip
                                label={`HR: ${round.heart_rate} bpm`}
                                color="info"
                              />
                            </Grid>
                          )}
                          {round.respiratory_rate && (
                            <Grid item xs={6} md={2}>
                              <Chip
                                label={`RR: ${round.respiratory_rate}/min`}
                                color="info"
                              />
                            </Grid>
                          )}
                          {round.oxygen_saturation && (
                            <Grid item xs={6} md={2}>
                              <Chip
                                label={`SpO2: ${round.oxygen_saturation}%`}
                                color="info"
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    )}
                    {Array.isArray(round.attachments) &&
                      round.attachments.length > 0 && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" gutterBottom>
                            {t("ipd.ward_rounds.attachments", "Attachments")}
                          </Typography>
                          <Grid container spacing={2}>
                            {round.attachments.map((att) => (
                              <Grid item key={att.id} xs={12} md={4}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  href={toAbsolute(att.file)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {att.caption || t("common.view", "View")}
                                </Button>
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Pagination */}
            {(Array.isArray(roundsData)
              ? roundsData.length
              : roundsData?.count || 0) > rowsPerPage && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={Math.ceil(
                    (Array.isArray(roundsData)
                      ? roundsData.length
                      : roundsData?.count || 0) / rowsPerPage
                  )}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        );
      })()}

      {/* Add/Edit Round Dialog */}
      <Dialog
        open={roundDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingRound
            ? t("ipd.ward_rounds.edit_round")
            : t("ipd.ward_rounds.add_round")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={Array.isArray(admissions) ? admissions : admissions?.results || []}
                getOptionLabel={(option) => {
                  if (typeof option === 'string' || typeof option === 'number') {
                    // If option is just an ID, find the admission
                    const admission = (Array.isArray(admissions) ? admissions : admissions?.results || [])
                      .find(a => a.id === option);
                    if (admission) {
                      return `${admission.patient_name || ""} - ${admission.bed_info || ""}${admission.ticket_number_display ? ` (Ticket: ${admission.ticket_number_display})` : ""}`.trim();
                    }
                    return "";
                  }
                  return `${option.patient_name || ""} - ${option.bed_info || ""}${option.ticket_number_display ? ` (Ticket: ${option.ticket_number_display})` : ""}`.trim();
                }}
                value={
                  (Array.isArray(admissions) ? admissions : admissions?.results || [])
                    .find(a => a.id === roundForm.admission) || null
                }
                onChange={(event, newValue) => {
                  setRoundForm({ ...roundForm, admission: newValue?.id || "" });
                  setFormErrors({ ...formErrors, admission: "" });
                }}
                onInputChange={(event, newInputValue) => {
                  setAdmissionSearch(newInputValue);
                }}
                loading={admissionsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("ipd.ward_rounds.admission")}
                    placeholder={t("ipd.ward_rounds.searchAdmissionPlaceholder", { defaultValue: "Search by patient name, phone, or ticket number..." })}
                    error={!!formErrors.admission}
                    helperText={formErrors.admission}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {admissionsLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {option.patient_name || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.bed_info || "N/A"}
                        {option.ticket_number_display && ` • Ticket: ${option.ticket_number_display}`}
                        {option.patient_phone && ` • Phone: ${option.patient_phone}`}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText={t("ipd.ward_rounds.noAdmissionsFound", { defaultValue: "No admissions found" })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.subjective")}
                multiline
                rows={3}
                value={roundForm.subjective}
                onChange={(e) => {
                  setRoundForm({ ...roundForm, subjective: e.target.value });
                  setFormErrors({ ...formErrors, subjective: "" });
                }}
                error={!!formErrors.subjective}
                helperText={formErrors.subjective}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.objective")}
                multiline
                rows={3}
                value={roundForm.objective}
                onChange={(e) => {
                  setRoundForm({ ...roundForm, objective: e.target.value });
                  setFormErrors({ ...formErrors, objective: "" });
                }}
                error={!!formErrors.objective}
                helperText={formErrors.objective}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.assessment")}
                multiline
                rows={3}
                value={roundForm.assessment}
                onChange={(e) => {
                  setRoundForm({ ...roundForm, assessment: e.target.value });
                  setFormErrors({ ...formErrors, assessment: "" });
                }}
                error={!!formErrors.assessment}
                helperText={formErrors.assessment}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.plan")}
                multiline
                rows={3}
                value={roundForm.plan}
                onChange={(e) => {
                  setRoundForm({ ...roundForm, plan: e.target.value });
                  setFormErrors({ ...formErrors, plan: "" });
                }}
                error={!!formErrors.plan}
                helperText={formErrors.plan}
              />
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {t("ipd.ward_rounds.apply_template", "Apply Template")}
                    </InputLabel>
                    <Select
                      value={selectedTemplateId}
                      onChange={(e) => {
                        const tid = e.target.value;
                        setSelectedTemplateId(tid);
                        const tpl = (
                          (Array.isArray(templates)
                            ? templates
                            : templates?.results) || []
                        ).find((x) => x.id === tid);
                        if (tpl) {
                          setRoundForm((prev) => ({
                            ...prev,
                            subjective: tpl.subjective || prev.subjective,
                            objective: tpl.objective || prev.objective,
                            assessment: tpl.assessment || prev.assessment,
                            plan: tpl.plan || prev.plan,
                          }));
                        }
                      }}
                    >
                      <MenuItem value="">{t("common.none", "None")}</MenuItem>
                      {(
                        (Array.isArray(templates)
                          ? templates
                          : templates?.results) || []
                      ).map((tpl) => (
                        <MenuItem key={tpl.id} value={tpl.id}>
                          {tpl.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button component="label" variant="outlined" fullWidth>
                    {t("ipd.ward_rounds.attach_files", "Attach Files")}
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={(e) =>
                        setSelectedFiles(Array.from(e.target.files || []))
                      }
                    />
                  </Button>
                  {selectedFiles?.length ? (
                    <Typography variant="caption" color="text.secondary">
                      {selectedFiles.length} file(s) selected
                    </Typography>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.ward_rounds.vital_signs")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.temperature")}
                type="number"
                value={roundForm.temperature}
                onChange={(e) =>
                  setRoundForm({ ...roundForm, temperature: e.target.value })
                }
                InputProps={{ endAdornment: "°C" }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.blood_pressure_systolic")}
                type="number"
                value={roundForm.blood_pressure_systolic}
                onChange={(e) =>
                  setRoundForm({
                    ...roundForm,
                    blood_pressure_systolic: e.target.value,
                  })
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.blood_pressure_diastolic")}
                type="number"
                value={roundForm.blood_pressure_diastolic}
                onChange={(e) =>
                  setRoundForm({
                    ...roundForm,
                    blood_pressure_diastolic: e.target.value,
                  })
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.heart_rate")}
                type="number"
                value={roundForm.heart_rate}
                onChange={(e) =>
                  setRoundForm({ ...roundForm, heart_rate: e.target.value })
                }
                InputProps={{ endAdornment: "bpm" }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.respiratory_rate")}
                type="number"
                value={roundForm.respiratory_rate}
                onChange={(e) =>
                  setRoundForm({
                    ...roundForm,
                    respiratory_rate: e.target.value,
                  })
                }
                InputProps={{ endAdornment: "/min" }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.oxygen_saturation")}
                type="number"
                value={roundForm.oxygen_saturation}
                onChange={(e) =>
                  setRoundForm({
                    ...roundForm,
                    oxygen_saturation: e.target.value,
                  })
                }
                InputProps={{ endAdornment: "%" }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.nursing_notes")}
                multiline
                rows={2}
                value={roundForm.nursing_notes}
                onChange={(e) =>
                  setRoundForm({ ...roundForm, nursing_notes: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.medication_changes")}
                multiline
                rows={2}
                value={roundForm.medication_changes}
                onChange={(e) =>
                  setRoundForm({
                    ...roundForm,
                    medication_changes: e.target.value,
                  })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("ipd.ward_rounds.special_instructions")}
                multiline
                rows={2}
                value={roundForm.special_instructions}
                onChange={(e) =>
                  setRoundForm({
                    ...roundForm,
                    special_instructions: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("common.cancel")}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t("common.save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Order from Round */}
      <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)}>
        <DialogTitle>
          {t("ipd.ward_rounds.create_order", "Create Order")}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 420 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>
                  {t("ipd.ward_rounds.order_type", "Order Type")}
                </InputLabel>
                <Select
                  value={orderForm.kind}
                  label={t("ipd.ward_rounds.order_type", "Order Type")}
                  onChange={(e) =>
                    setOrderForm({ ...orderForm, kind: e.target.value })
                  }
                >
                  <MenuItem value="lab">
                    {t("ipd.ward_rounds.lab", "Lab")}
                  </MenuItem>
                  <MenuItem value="xray">
                    {t("ipd.ward_rounds.xray", "X-Ray")}
                  </MenuItem>
                  <MenuItem value="ultrasound">
                    {t("ipd.ward_rounds.ultrasound", "Ultrasound")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {orderForm.kind === "lab" && (
              <>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={
                      (Array.isArray(labTests)
                        ? labTests
                        : labTests?.results) || []
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(opt) =>
                      `${opt.name}${opt.cost ? ` — ${opt.cost} AFN` : ""}`
                    }
                    filterOptions={(x) => x}
                    value={selectedLabTests}
                    onChange={(_, newValue) => {
                      setSelectedLabTests(newValue);
                      setOrderForm({
                        ...orderForm,
                        tests: newValue.map((v) => v.id),
                      });
                    }}
                    onInputChange={(_, newInput) => setTestSearch(newInput)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("lab.tests", "Tests")}
                        placeholder={t("common.search", "Search")}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={`${option.name}${
                            option.cost ? ` — ${option.cost} AFN` : ""
                          }`}
                        />
                      ))
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t("common.urgency", "Urgency")}</InputLabel>
                    <Select
                      value={orderForm.urgency ? "urgent" : "normal"}
                      onChange={(e) =>
                        setOrderForm({
                          ...orderForm,
                          urgency: e.target.value === "urgent",
                        })
                      }
                    >
                      <MenuItem value="normal">
                        {t("common.normal", "Normal")}
                      </MenuItem>
                      <MenuItem value="urgent">
                        {t("common.urgent", "Urgent")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("common.discount", "Discount")}
                    type="number"
                    value={orderForm.discount}
                    onChange={(e) =>
                      setOrderForm({ ...orderForm, discount: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">
                      {t("lab.subtotal", "Subtotal")}:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedLabTests
                        .map((t) => parseFloat(t.cost || 0))
                        .reduce(
                          (a, b) => (parseFloat(a) || 0) + (parseFloat(b) || 0),
                          0
                        )}{" "}
                      AFN
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">
                      {t("common.discount", "Discount")}:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>{`${
                      orderForm.discount || 0
                    } AFN`}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle2">
                      {t("common.total", "Total")}:
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {(() => {
                        const subtotal = selectedLabTests
                          .map((t) => parseFloat(t.cost || 0))
                          .reduce(
                            (a, b) =>
                              (parseFloat(a) || 0) + (parseFloat(b) || 0),
                            0
                          );
                        const total = Math.max(
                          0,
                          subtotal - (parseFloat(orderForm.discount) || 0)
                        );
                        return `${total} AFN`;
                      })()}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

            {orderForm.kind === "xray" && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>{t("xray.type", "X-Ray Type")}</InputLabel>
                    <Select
                      value={orderForm.xray_type}
                      onChange={(e) =>
                        setOrderForm({
                          ...orderForm,
                          xray_type: e.target.value,
                        })
                      }
                    >
                      {(Array.isArray(xrayTypes)
                        ? xrayTypes
                        : xrayTypes?.results || []
                      ).map((x) => (
                        <MenuItem key={x.id} value={x.id}>
                          {x.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("xray.notes", "Doctor Notes")}
                    value={orderForm.doctor_notes}
                    onChange={(e) =>
                      setOrderForm({
                        ...orderForm,
                        doctor_notes: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>{t("common.urgency", "Urgency")}</InputLabel>
                    <Select
                      value={orderForm.is_urgent ? "urgent" : "normal"}
                      onChange={(e) =>
                        setOrderForm({
                          ...orderForm,
                          is_urgent: e.target.value === "urgent",
                        })
                      }
                    >
                      <MenuItem value="normal">
                        {t("common.normal", "Normal")}
                      </MenuItem>
                      <MenuItem value="urgent">
                        {t("common.urgent", "Urgent")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {orderForm.kind === "ultrasound" && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {t("ultrasound.type", "Ultrasound Type")}
                    </InputLabel>
                    <Select
                      value={orderForm.ultrasound_type}
                      onChange={(e) =>
                        setOrderForm({
                          ...orderForm,
                          ultrasound_type: e.target.value,
                        })
                      }
                    >
                      {(Array.isArray(ultrasoundTypes)
                        ? ultrasoundTypes
                        : ultrasoundTypes?.results || []
                      ).map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t("common.urgency", "Urgency")}</InputLabel>
                    <Select
                      value={orderForm.is_urgent ? "urgent" : "normal"}
                      onChange={(e) =>
                        setOrderForm({
                          ...orderForm,
                          is_urgent: e.target.value === "urgent",
                        })
                      }
                    >
                      <MenuItem value="normal">
                        {t("common.normal", "Normal")}
                      </MenuItem>
                      <MenuItem value="urgent">
                        {t("common.urgent", "Urgent")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("common.amount", "Amount")}
                    type="number"
                    value={orderForm.total_amount}
                    onChange={(e) =>
                      setOrderForm({
                        ...orderForm,
                        total_amount: e.target.value,
                      })
                    }
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={
              labOrderMutation.isPending ||
              xrayOrderMutation.isPending ||
              ultrasoundOrderMutation.isPending
            }
            onClick={() => {
              if (!orderRound) return;
              if (orderForm.kind === "lab") {
                labOrderMutation.mutate({
                  id: orderRound.id,
                  data: {
                    tests: orderForm.tests,
                    urgency: orderForm.urgency,
                    discount: orderForm.discount,
                  },
                });
              } else if (orderForm.kind === "xray") {
                if (!orderForm.xray_type) return;
                xrayOrderMutation.mutate({
                  id: orderRound.id,
                  data: {
                    xray_type: orderForm.xray_type,
                    is_urgent: orderForm.is_urgent,
                    doctor_notes: orderForm.doctor_notes,
                  },
                });
              } else {
                if (!orderForm.ultrasound_type || !orderForm.total_amount)
                  return;
                ultrasoundOrderMutation.mutate({
                  id: orderRound.id,
                  data: {
                    ultrasound_type: orderForm.ultrasound_type,
                    is_urgent: orderForm.is_urgent,
                    total_amount: orderForm.total_amount,
                  },
                });
              }
            }}
          >
            {labOrderMutation.isPending ||
            xrayOrderMutation.isPending ||
            ultrasoundOrderMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t("common.save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("ipd.ward_rounds.delete_confirm_title")}</DialogTitle>
        <DialogContent>
          <Typography>{t("ipd.ward_rounds.delete_confirm_message")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t("common.delete")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WardRoundsList;
